/**
 * X3DH — acordo de chaves assincrono.
 *
 * Permite a Alice comecar a conversa com o Bob **offline**, usando as
 * pre-chaves que ele publicou. Quatro Diffie-Hellman entram no mesmo KDF:
 *
 *   DH1 = DH(IK_A, SPK_B)   autentica Alice
 *   DH2 = DH(EK_A, IK_B)    autentica Bob
 *   DH3 = DH(EK_A, SPK_B)   sigilo futuro
 *   DH4 = DH(EK_A, OPK_B)   sigilo por sessao (opcional)
 */
import { concat, equalConstantTime } from './bytes.js'
import type { Identity } from './identity.js'
import { verifySignature } from './identity.js'
import { FingerprintMismatchError, PreKeyUnavailableError } from './errors.js'
import { DOMAIN, kdf } from './kdf.js'
import type { PreKeyBundle, PreKeyStore } from './prekeys.js'
import { SIZES, sodium } from './sodium.js'

/** Cabecalho que acompanha a primeira mensagem, para o Bob refazer o calculo. */
export interface X3dhInitialMessage {
  readonly initiatorSignPublicKey: Uint8Array
  readonly initiatorAgreePublicKey: Uint8Array
  readonly ephemeralPublicKey: Uint8Array
  readonly signedPreKeyId: number
  readonly oneTimePreKeyId?: number
}

export interface X3dhResult {
  readonly sharedSecret: Uint8Array
  /** `AD` do AEAD: amarra a sessao as duas identidades. */
  readonly associatedData: Uint8Array
  readonly initiatorHeaderKey: Uint8Array
  readonly responderHeaderKey: Uint8Array
}

export interface X3dhInitiateResult extends X3dhResult {
  readonly initialMessage: X3dhInitialMessage
  /** Chave publica do ratchet do outro lado (a Signed PreKey dele). */
  readonly remoteRatchetPublicKey: Uint8Array
}

function deriveSessionKeys(
  dhOutputs: readonly Uint8Array[],
  initiatorFingerprint: Uint8Array,
  responderFingerprint: Uint8Array,
): X3dhResult {
  const sharedSecret = sodium().crypto_generichash(
    SIZES.symmetricKey,
    concat(DOMAIN.x3dh, ...dhOutputs),
  )
  return {
    sharedSecret,
    associatedData: concat(initiatorFingerprint, responderFingerprint),
    initiatorHeaderKey: kdf(sharedSecret, DOMAIN.headerKeyInitiator, SIZES.symmetricKey),
    responderHeaderKey: kdf(sharedSecret, DOMAIN.headerKeyResponder, SIZES.symmetricKey),
  }
}

/**
 * Lado de quem inicia (Alice), a partir do bundle publicado pelo Bob.
 *
 * `expectedFingerprint` e o fingerprint que a Alice ja conhece do Bob (do
 * convite ou de um pareamento anterior). E obrigatorio de proposito: um
 * parametro opcional aqui seria um convite a esquece-lo, e esquece-lo e
 * exatamente o que entrega a sessao para o servidor. Quem nao conhece o
 * fingerprint do outro lado ainda nao tem com quem falar.
 */
export function initiate(
  self: Identity,
  bundle: PreKeyBundle,
  expectedFingerprint: Uint8Array,
): X3dhInitiateResult {
  // Sem esta verificacao, um servidor malicioso trocaria a SPK e leria tudo.
  verifySignature(
    bundle.signedPreKey.signature,
    bundle.signedPreKey.publicKey,
    bundle.signPublicKey,
    'signed prekey do destinatario',
  )

  const s = sodium()
  const responderFingerprint = s.crypto_generichash(
    SIZES.fingerprint,
    concat(bundle.signPublicKey, bundle.agreePublicKey),
  )
  // As chaves do bundle tem de hashear para o fingerprint que ja conhecemos.
  // Conferir DEPOIS dos DH seria tarde: a OTPK do outro lado ja teria sido
  // consumida por um bundle falso.
  if (!equalConstantTime(responderFingerprint, expectedFingerprint)) {
    throw new FingerprintMismatchError()
  }

  const ephemeral = s.crypto_box_keypair()
  const spk = bundle.signedPreKey.publicKey

  const dh1 = self.agree(spk)
  const dh2 = s.crypto_scalarmult(ephemeral.privateKey, bundle.agreePublicKey)
  const dh3 = s.crypto_scalarmult(ephemeral.privateKey, spk)
  const outputs = [dh1, dh2, dh3]
  if (bundle.oneTimePreKey !== undefined) {
    outputs.push(s.crypto_scalarmult(ephemeral.privateKey, bundle.oneTimePreKey.publicKey))
  }

  const session = deriveSessionKeys(outputs, self.fingerprint, responderFingerprint)

  const initialMessage: X3dhInitialMessage =
    bundle.oneTimePreKey === undefined
      ? {
          initiatorSignPublicKey: self.publicIdentity.signPublicKey,
          initiatorAgreePublicKey: self.publicIdentity.agreePublicKey,
          ephemeralPublicKey: ephemeral.publicKey,
          signedPreKeyId: bundle.signedPreKey.keyId,
        }
      : {
          initiatorSignPublicKey: self.publicIdentity.signPublicKey,
          initiatorAgreePublicKey: self.publicIdentity.agreePublicKey,
          ephemeralPublicKey: ephemeral.publicKey,
          signedPreKeyId: bundle.signedPreKey.keyId,
          oneTimePreKeyId: bundle.oneTimePreKey.keyId,
        }

  return { ...session, initialMessage, remoteRatchetPublicKey: spk }
}

/** Lado de quem responde (Bob), quando a primeira mensagem chega. */
export function respond(
  self: Identity,
  preKeys: PreKeyStore,
  message: X3dhInitialMessage,
): X3dhResult {
  const s = sodium()
  const spk = preKeys.signedPreKey

  const dh1 = s.crypto_scalarmult(spk.privateKey, message.initiatorAgreePublicKey)
  const dh2 = self.agree(message.ephemeralPublicKey)
  const dh3 = s.crypto_scalarmult(spk.privateKey, message.ephemeralPublicKey)
  const outputs = [dh1, dh2, dh3]

  if (message.oneTimePreKeyId !== undefined) {
    // A OTPK e consumida aqui e nunca mais existe — e isso que da sigilo
    // por sessao mesmo se a chave de identidade vazar depois.
    const otp = preKeys.takeOneTimePreKey(message.oneTimePreKeyId)
    if (otp === undefined) throw new PreKeyUnavailableError(message.oneTimePreKeyId)
    outputs.push(s.crypto_scalarmult(otp.privateKey, message.ephemeralPublicKey))
  }

  const initiatorFingerprint = s.crypto_generichash(
    SIZES.fingerprint,
    concat(message.initiatorSignPublicKey, message.initiatorAgreePublicKey),
  )
  return deriveSessionKeys(outputs, initiatorFingerprint, self.fingerprint)
}
